/**
 * Helpers puramente funcionales para extraer metadatos de un documento
 * OPF (Open Packaging Format) de un EPUB.
 *
 * Son funciones puras (sin I/O) que reciben XML ya cargado en un
 * DOMParser. Mantenerlas separadas facilita testearlas por unidad.
 */

/** Representación mínima de un OPF de EPUB. */
export interface ParsedEpub {
  title: string
  author: string
  publisher: string | null
  language: string | null
  isbn: string | null
  description: string | null
  publishDate: string | null
  /**
   * Géneros/categorías extraídos de los `<dc:subject>` del OPF. Trimeados,
   * sin vacíos ni duplicados. Se vuelcan a `metadata.tags` al importar para
   * alimentar los filtros de etiquetas existentes.
   */
  subjects: string[]
  /** Href (relativo al OPF) de la imagen de portada. */
  coverHref: string | null
}

const ISBN_SCHEMES = new Set([
  'isbn',
  'urn:isbn',
  'isbn:',
  'gtin:13',
])

/**
 * Busca todos los elementos cuyo `localName` coincide, sin importar el
 * prefijo o namespace. Usamos esto en vez de `getElementsByTagNameNS`
 * porque jsdom no siempre resuelve bien los namespaces al parsear XML
 * suelto, y los OPF reales mezclan prefijos (dc:, dc1:, sin prefijo…).
 */
function getByLocalName(doc: Document, localName: string): Element[] {
  const all = doc.getElementsByTagName('*')
  const out: Element[] = []
  for (let i = 0; i < all.length; i++) {
    const el = all[i]
    // localName funciona en XML; fallback a nodeName sin prefijo.
    const ln = el.localName ?? el.nodeName.split(':').pop() ?? ''
    if (ln.toLowerCase() === localName) out.push(el)
  }
  return out
}

/**
 * Busca el ISBN entre los `<dc:identifier>` del OPF. El EPUB puede
 * declararlo de tres formas:
 *   1. `<dc:identifier opf:scheme="isbn">123</dc:identifier>`
 *   2. `<dc:identifier>urn:isbn:123</dc:identifier>`
 *   3. `<dc:identifier id="...">123</dc:identifier>` (sin scheme claro)
 */
export function extractIsbn(doc: Document): string | null {
  const nodes = getByLocalName(doc, 'identifier')
  for (const node of nodes) {
    const raw = node.textContent?.trim() ?? ''
    const scheme = (
      node.getAttribute('opf:scheme') ??
      node.getAttributeNS('http://www.idpf.org/2007/opf', 'scheme') ??
      ''
    ).toLowerCase()

    if (scheme && ISBN_SCHEMES.has(scheme)) {
      return normalizeIsbn(raw)
    }
    const urnMatch = raw.match(/^urn:isbn:(.+)$/i)
    if (urnMatch) return normalizeIsbn(urnMatch[1])
  }
  // Último recurso: si algún identifier parece un ISBN por su formato.
  for (const node of nodes) {
    const raw = node.textContent?.trim() ?? ''
    if (/^[0-9]{9}[0-9Xx]$|^[0-9]{13}$/.test(raw.replace(/[-\s]/g, ''))) {
      return normalizeIsbn(raw)
    }
  }
  return null
}

/** Quita guiones/espacios y normaliza a mayúsculas (X de check digit). */
function normalizeIsbn(raw: string): string {
  const clean = raw.replace(/[-\s]/g, '')
  return clean.toUpperCase()
}

/** Lee el primer `<dc:title>`. */
export function extractTitle(doc: Document): string {
  const node = getByLocalName(doc, 'title')[0]
  return (node?.textContent ?? '').trim() || 'Sin título'
}

/** Lee el primer `<dc:creator>`, que por convención es el autor. */
export function extractAuthor(doc: Document): string {
  const node = getByLocalName(doc, 'creator')[0]
  return (node?.textContent ?? '').trim() || 'Autor desconocido'
}

export function extractPublisher(doc: Document): string | null {
  const node = getByLocalName(doc, 'publisher')[0]
  const text = (node?.textContent ?? '').trim()
  return text || null
}

export function extractLanguage(doc: Document): string | null {
  const node = getByLocalName(doc, 'language')[0]
  const text = (node?.textContent ?? '').trim()
  return text || null
}

export function extractDescription(doc: Document): string | null {
  const node = getByLocalName(doc, 'description')[0]
  const text = (node?.textContent ?? '').trim()
  return text || null
}

export function extractPublishDate(doc: Document): string | null {
  const node = getByLocalName(doc, 'date')[0]
  const text = (node?.textContent ?? '').trim()
  return text || null
}

/**
 * Lee todos los `<dc:subject>` como géneros/categorías. Trimea, omite
 * vacíos y deduplica conservando el orden de aparición. Un mismo EPUB puede
 * declarar varios subjects (p. ej. "Ficción", "Aventuras", "Ficción").
 */
export function extractSubjects(doc: Document): string[] {
  const nodes = getByLocalName(doc, 'subject')
  const seen = new Set<string>()
  const out: string[] = []
  for (const node of nodes) {
    const text = (node.textContent ?? '').trim()
    if (!text) continue
    const key = text.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(text)
  }
  return out
}

/**
 * Determina el href de la portada dentro del EPUB. Estrategia en orden:
 *   1. `<meta name="cover" content="coverId">` → buscar `manifest#coverId`.
 *   2. Item cuyo id es "cover" o "cover-image".
 *   3. Item con properties="cover-image" (EPUB 3).
 *   4. Item cuyo href contiene "cover" (heurístico).
 *
 * Devuelve el href TAL CUAL aparece en el OPF (relativo a él); el caller
 * debe resolverlo contra la ubicación del OPF dentro del zip.
 */
export function extractCoverHref(doc: Document): string | null {
  // 1. meta name="cover" (EPUB 2 clásico)
  const metas = Array.from(doc.getElementsByTagName('meta'))
  for (const meta of metas) {
    if (meta.getAttribute('name')?.toLowerCase() === 'cover') {
      const coverId = meta.getAttribute('content')
      if (coverId) {
        const byId = findItemById(doc, coverId)
        if (byId) return byId
      }
    }
  }

  // 2 y 3. properties="cover-image" o id clásico
  const items = Array.from(doc.getElementsByTagName('item'))
  const byProperties = items.find((it) =>
    (it.getAttribute('properties') ?? '').split(/\s+/).includes('cover-image'),
  )
  if (byProperties) return byProperties.getAttribute('href')

  const byId = items.find((it) => /^(cover(-image)?)$/i.test(it.getAttribute('id') ?? ''))
  if (byId) return byId.getAttribute('href')

  // 4. Heurístico: href que contiene "cover" y es imagen.
  const byHref = items.find((it) => {
    const href = (it.getAttribute('href') ?? '').toLowerCase()
    const media = (it.getAttribute('media-type') ?? '').toLowerCase()
    return href.includes('cover') && media.startsWith('image/')
  })
  if (byHref) return byHref.getAttribute('href')

  return null
}

function findItemById(doc: Document, id: string): string | null {
  const item = doc.querySelector(`item[id="${cssEscape(id)}"]`)
  return item?.getAttribute('href') ?? null
}

/** Escapa un valor para usarlo dentro de un selector de atributo CSS. */
function cssEscape(value: string): string {
  // Suficiente para ids de OPF (no contendrán comillas dobles).
  return value.replace(/["\\]/g, '\\$&')
}

/** Une todos los extractores en una sola pasada sobre el documento. */
export function parseOpfDocument(doc: Document): ParsedEpub {
  return {
    title: extractTitle(doc),
    author: extractAuthor(doc),
    publisher: extractPublisher(doc),
    language: extractLanguage(doc),
    isbn: extractIsbn(doc),
    description: extractDescription(doc),
    publishDate: extractPublishDate(doc),
    subjects: extractSubjects(doc),
    coverHref: extractCoverHref(doc),
  }
}
